/**
 * @fileoverview "Log in with Clever" button (AR-925)
 * @description Starts the Clever Instant Login redirect. Renders nothing when
 * the build has no `REACT_APP_CLEVER_CLIENT_ID`, so environments without a
 * Clever app keep the sign-in card unchanged.
 */

import { Button } from '@mantine/core'
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cleverAuthorizeUrl, cleverClientId } from 'services/cleverSso'
import { CleverIcon } from './CleverIcon'

interface CleverButtonProps {
  readonly disabled?: boolean
}

export const CleverButton = memo(({ disabled }: CleverButtonProps) => {
  const { t } = useTranslation()
  // Stays true until the page unloads: the redirect takes a moment and the
  // button must not look dead (same treatment as the Classroom connect button)
  const [redirecting, setRedirecting] = useState(false)
  const clientId = cleverClientId()

  if (!clientId) return null

  const handleClick = () => {
    setRedirecting(true)
    window.location.assign(cleverAuthorizeUrl(clientId))
  }

  return (
    <Button
      fullWidth
      mt='md'
      size='lg'
      variant='default'
      leftIcon={<CleverIcon />}
      disabled={disabled}
      loading={redirecting}
      onClick={handleClick}
      aria-label={t('account_signIn_login_with_clever')}
    >
      {t('account_signIn_login_with_clever')}
    </Button>
  )
})

CleverButton.displayName = 'CleverButton'
